import {useState} from "react";
import {Link, useNavigate} from "react-router-dom";

import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {
  Loader2Icon,
  MoreHorizontalIcon,
  PlusIcon,
  PowerIcon,
  RotateCwIcon,
  ServerIcon,
  Trash2Icon,
} from "lucide-react";
import {toast} from "sonner";

import {
  type Server,
  serversDestroy,
  serversList,
  serversReboot,
  serversStart,
  serversStop,
} from "@/sdk";

import {Badge} from "@/components/ui/badge";
import {Button} from "@/components/ui/button";
import {Card, CardContent} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import {formatGB, formatMB, formatRelative, serverStateVariant} from "@/lib/format";

const SERVERS_QUERY_KEY = ["servers", "list"] as const;

export const ServersList = () => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [destroyTarget, setDestroyTarget] = useState<Server | null>(null);

  const query = useQuery({
    queryKey: SERVERS_QUERY_KEY,
    queryFn: async () => {
      const {data, error} = await serversList();
      if (error) throw error;
      return data?.items ?? [];
    },
    refetchInterval: 5000,
  });

  const invalidate = () => qc.invalidateQueries({queryKey: SERVERS_QUERY_KEY});

  const startM = useMutation({
    mutationFn: (id: string) => serversStart({path: {id}}).then((r) => r.data),
    onSuccess: () => {
      toast.success("Server starting");
      invalidate();
    },
    onError: () => toast.error("Failed to start server"),
  });
  const stopM = useMutation({
    mutationFn: (id: string) => serversStop({path: {id}, body: {force: false}}).then((r) => r.data),
    onSuccess: () => {
      toast.success("Server stopping");
      invalidate();
    },
    onError: () => toast.error("Failed to stop server"),
  });
  const rebootM = useMutation({
    mutationFn: (id: string) =>
      serversReboot({path: {id}, body: {force: false}}).then((r) => r.data),
    onSuccess: () => {
      toast.success("Server rebooting");
      invalidate();
    },
    onError: () => toast.error("Failed to reboot server"),
  });
  const destroyM = useMutation({
    mutationFn: (id: string) => serversDestroy({path: {id}}).then((r) => r.data),
    onSuccess: () => {
      toast.success("Server destroyed");
      setDestroyTarget(null);
      invalidate();
    },
    onError: () => toast.error("Failed to destroy server"),
  });

  const servers = query.data ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold">Servers</h1>
          <p className="text-sm text-muted-foreground">
            Manage virtual machines in your organization. Updates poll every 5s.
          </p>
        </div>
        <Button asChild>
          <Link to="/servers/new">
            <PlusIcon /> Create server
          </Link>
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {query.isLoading ? (
            <div className="flex items-center justify-center p-12 text-muted-foreground">
              <Loader2Icon className="size-5 animate-spin" />
            </div>
          ) : query.isError ? (
            <div className="p-6 text-sm text-destructive">
              Failed to load servers.{" "}
              <button
                type="button"
                className="underline"
                onClick={() => query.refetch()}
              >
                Retry
              </button>
            </div>
          ) : servers.length === 0 ? (
            <EmptyServers onCreate={() => navigate("/servers/new")} />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Hostname</TableHead>
                  <TableHead>State</TableHead>
                  <TableHead>vCPU / Mem / Disk</TableHead>
                  <TableHead>Primary IPv4</TableHead>
                  <TableHead>Last seen</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {servers.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell>
                      <Link
                        to={`/servers/${s.id}`}
                        className="font-medium hover:underline"
                      >
                        {s.hostname}
                      </Link>
                      {s.label ? (
                        <div className="text-xs text-muted-foreground">{s.label}</div>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <Badge variant={serverStateVariant(s.state)} className="capitalize">
                        {s.state || "unknown"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {s.vcpus} vCPU · {formatMB(Math.round(s.memory_bytes / (1024 * 1024)))} ·{" "}
                      {formatGB(Math.round(s.disk_bytes / (1024 * 1024 * 1024)))}
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {s.primary_ipv4 ?? "—"}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {formatRelative(s.last_seen_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon-sm">
                            <MoreHorizontalIcon />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuLabel>{s.hostname}</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            disabled={startM.isPending || s.state === "running"}
                            onSelect={() => startM.mutate(s.id)}
                          >
                            <PowerIcon /> Start
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            disabled={stopM.isPending || s.state === "stopped"}
                            onSelect={() => stopM.mutate(s.id)}
                          >
                            <PowerIcon /> Stop
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            disabled={rebootM.isPending}
                            onSelect={() => rebootM.mutate(s.id)}
                          >
                            <RotateCwIcon /> Reboot
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            variant="destructive"
                            onSelect={() => setDestroyTarget(s)}
                          >
                            <Trash2Icon /> Destroy…
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={destroyTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDestroyTarget(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Destroy server?</DialogTitle>
            <DialogDescription>
              This will terminate <span className="font-mono">{destroyTarget?.hostname}</span>{" "}
              and remove all associated storage. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDestroyTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={destroyM.isPending}
              onClick={() => destroyTarget && destroyM.mutate(destroyTarget.id)}
            >
              {destroyM.isPending ? "Destroying…" : "Destroy"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

function EmptyServers({onCreate}: {onCreate: () => void}) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 p-12 text-center">
      <ServerIcon className="size-10 text-muted-foreground" />
      <div className="space-y-1">
        <h3 className="font-medium">No servers yet</h3>
        <p className="text-sm text-muted-foreground">Create your first VM to get started.</p>
      </div>
      <Button onClick={onCreate}>
        <PlusIcon /> Create server
      </Button>
    </div>
  );
}
